<template>
  <div class="card-item">
    <!-- Drag Handle -->
    <v-tooltip left color="surface_neutral_selected">
      <template v-slot:activator="{ on, attrs }">
        <div
          class="card-item__drag"
          role="button"
          tabindex="0"
          aria-label="Arraste para mover este item"
          v-bind="attrs"
          v-on="on"
        >
          <v-icon color="text_secondary">mdi-drag</v-icon>
        </div>
      </template>
      <span>Segure e arraste para a coluna de exibição</span>
    </v-tooltip>

    <!-- Body -->
    <div class="card-item__body">
      <div class="card-item__title" style="font-size: 1.1rem; font-weight: 600;">
        {{ item.dsc_titulo }}
      </div>

      <div class="card-item__info">
        {{ item.dsc_descricao }}
      </div>

      <div class="cronometro-timer">
        <span class="cronometro-timer__display">
          {{ item.tempo.split(":").at(0) }}:{{ item.tempo.split(":").at(1) }}:{{ item.tempo.split(":").at(2) }}
        </span>
      </div>

      <div class="card-item__meta">
        <span>{{ formatDate(item.created_at) }}</span>
        <span class="meta-separator">&bull;</span>
        <span>{{ formatHour(item.created_at) }}</span>
      </div>
    </div>

    <!-- Actions -->
    <div class="card-item__actions">
      <v-tooltip left color="surface_neutral_selected">
        <template v-slot:activator="{ on, attrs }">
          <button
            class="card-action-box card-action-box--move"
            v-bind="attrs"
            v-on="on"
            @click="setCronometroExibir"
            aria-label="Enviar para exibição">
            <v-icon>mdi-arrow-right</v-icon>
          </button>
        </template>
        <span>Enviar para exibição</span>
      </v-tooltip>

      <ModalEditarCronometro
        :cronometro-data="item"
        :isFavoritaTab="isFavoritaTab"
        :currentPage="currentPage"
      />

      <ModalExcluirCronometro :cronometro="item" />

      <v-tooltip left color="surface_neutral_selected">
        <template v-slot:activator="{ on, attrs }">
          <button
            class="card-action-box card-action-box--favorite"
            :class="{ 'is-bursting': bursting }"
            :aria-label="item.flg_favorita ? 'Desfavoritar cronômetro' : 'Favoritar cronômetro'"
            v-bind="attrs"
            v-on="on"
            @click.prevent="handleFavoritarClick"
          >
            <v-icon v-if="!item.flg_favorita && !bursting">mdi-heart-outline</v-icon>
            <v-icon v-else>mdi-heart</v-icon>
            <span v-for="i in 5" :key="`hp-${i}`" :class="`heart-particle heart-particle--${i}`" aria-hidden="true">♥</span>
          </button>
        </template>
        <span>{{ item.flg_favorita ? "Desfavoritar cronômetro" : "Favoritar cronômetro" }}</span>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
import { apiClient } from "@/services/api";
import { capitalizeString } from "@/utils/capitalizeString";
import { formatDate, formatHour } from "@/utils/dateUtils";
import { mapGetters } from "vuex";
import ModalEditarCronometro from "@/components/Modal/Cronometro/ModalEditarCronometro.vue";
import ModalExcluirCronometro from "@/components/Modal/Cronometro/ModalExcluirCronometro.vue";
export default {
  props: {
    item: Object,
    getCronometrosProducao: Function,
    getCronometroEmExibicao: Function,
    currentPage: Number,
    isFavoritaTab: Boolean,
  },
  components: {
    ModalEditarCronometro,
    ModalExcluirCronometro,
  },

  data() {
    return {
      isModalImagemAprovadasVisible: false,
      midia_url_aprovadas: "",
      media_type: "",
      bursting: false,
    };
  },
  computed: {
    breakpoint() {
      return this.$vuetify.breakpoint.name;
    },
    cod_producao() {
      return this.$store.state.producoes.selectedProducao.cod_producao;
    },
    ...mapGetters({
      cod_pasta: "pastas/getterCodPasta",
    }),
  },
  methods: {
    formatDate,
    formatHour,
    async showModalImagem(midia_url_aprovadas) {
      this.isModalImagemAprovadasVisible = true;
      this.midia_url_aprovadas = midia_url_aprovadas;
    },
    async closeModalImagem() {
      this.isModalImagemAprovadasVisible = false;
    },
    capitalize(string) {
      return capitalizeString(string);
    },
    startDrag(evt, item) {
      evt.dataTransfer.dropEffect = "move";
      evt.dataTransfer.effectAllowed = "move";
      evt.dataTransfer.setData("text/plain", JSON.stringify(item));
    },
    async setCronometroExibir() {
      const cod_producao = this.$store.state.producoes.selectedProducao.cod_producao;
      const id = this.item.id;

      // Optimistic: drop it from the received list right away so the move feels instant.
      this.$store.commit("cronometros/removeCronometroProducao", id);

      try {
        await apiClient.patch(`/cronometro/setCronometroExibir/${id}/${cod_producao}`);
        await this.getCronometroEmExibicao({ cod_producao });

        // Only refetch if removing it emptied the current page while items remain
        // elsewhere — otherwise the list stays put with no jump.
        const list = this.$store.state.cronometros.cronometrosProducao;
        if (list && list.items && list.items.length === 0 && list.totalItems > 0) {
          await this.refetchRecebidos(cod_producao);
        }
      } catch (error) {
        console.error(error);
        // Move failed — reconcile the list with the server.
        await this.refetchRecebidos(cod_producao);
      }
    },
    refetchRecebidos(cod_producao) {
      return this.getCronometrosProducao({
        cod_producao,
        flg_favorita: this.isFavoritaTab,
        page: 1,
        size: 5,
        filtro_texto: this.$store.state.cronometros.filtroCronometros,
        cod_pasta: this.cod_pasta,
      });
    },

    handleFavoritarClick() {
      // Heart-burst flourish only when un-favorited → favorited.
      if (!this.item.flg_favorita) {
        this.bursting = true;
        setTimeout(() => { this.bursting = false; }, 900);
      }
      this.favoritarCronometro();
    },

    async favoritarCronometro() {
      const cod_producao = this.$store.state.producoes.selectedProducao.cod_producao;
      const id_cronometro = this.item.id;
      if (!this.item.flg_favorita) {
        await apiClient
          .patch(`cronometro/setCronometroFavorito/${id_cronometro}/${cod_producao}`)
          .catch((error) => {
            console.error(error);
          })
          .finally(async () => {
            await this.getCronometrosProducao({
              cod_producao: this.$store.state.producoes.selectedProducao.cod_producao,
              flg_favorita: this.isFavoritaTab,
              page: this.$store.state.cronometros.cronometrosProducao.currentPage,
              size: 5,
              filtro_texto: this.$store.state.cronometros.filtroCronometros,
              cod_pasta: this.cod_pasta,
            });
          });
      } else {
        await apiClient
          .patch(`cronometro/removerCronometroFavorito/${id_cronometro}/${cod_producao}`)
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.getCronometrosProducao({
              cod_producao: this.$store.state.producoes.selectedProducao.cod_producao,
              flg_favorita: this.isFavoritaTab,
              page: this.$store.state.cronometros.cronometrosProducao.currentPage,
              size: 5,
              filtro_texto: this.$store.state.cronometros.filtroCronometros,
              cod_pasta: this.cod_pasta,
            });
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.cronometro-timer {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 0;
  flex: 1;
}

.cronometro-timer__display {
  font-size: 3.5rem;
  font-weight: 600;
  color: var(--v-text_primary-base);
  letter-spacing: 2px;
  line-height: 1;
}
</style>
